import { useEffect, useState } from 'react';
import useAuth from './useAuth';
import useAxiosPrivate from './useAxiosPrivate';
import { useAppSelector } from '../redux/helper';
import { getCustomer, parseCustomerResponse } from '../api/backofficeCustomers';
import type { OrderCustomerContact } from '../utils/orderCustomerContact';

// Phones by customer id for this app session: orders arrive with their
// customer's phone, and a customer picked at checkout is looked up. Only
// phones are kept: a customer without one is looked up again when picked, so
// a phone saved since (a paid M-Pesa prompt, Back Office) shows.
const phoneByCustomerId = new Map<string, string>();

export const rememberCustomerPhone = (
  customerId: string | null | undefined,
  phone: string | null | undefined,
) => {
  if (!customerId) return;
  const id = String(customerId);
  const trimmed = String(phone || '').trim();
  if (trimmed) phoneByCustomerId.set(id, trimmed);
  else phoneByCustomerId.delete(id);
};

/** For tests. */
export const forgetCustomerPhones = () => phoneByCustomerId.clear();

/**
 * The phone of the customer with `customerId`: the one the order came with
 * when it is that customer, else what this session already knows, else a
 * lookup. Null while unknown, for a walk in, or when the customer has none;
 * never the previous customer's phone while the next is looked up.
 */
export const useCustomerPhone = (
  customerId: string | null,
  known?: OrderCustomerContact,
): string | null => {
  const axiosprivate = useAxiosPrivate();
  const { auth } = useAuth();
  const locationId = useAppSelector(state =>
    String(state.location.currentLocation?._id || ''),
  );
  // Draws again once a lookup has found a phone.
  const [, setFound] = useState(0);

  const orderPhone =
    customerId && known?.customerId === customerId
      ? String(known.customerPhone || '').trim()
      : '';

  useEffect(() => {
    if (!customerId) return;
    if (orderPhone) {
      phoneByCustomerId.set(customerId, orderPhone);
      return;
    }
    if (phoneByCustomerId.has(customerId)) return;
    if (!locationId || !auth?.accessToken) return;
    let cancelled = false;
    getCustomer(axiosprivate as any, locationId, customerId, {
      accessToken: auth.accessToken,
    })
      .then(res => {
        const phone = String(
          parseCustomerResponse(res)?.customer?.bp_customer_phone_number || '',
        ).trim();
        if (!phone) return;
        phoneByCustomerId.set(customerId, phone);
        if (!cancelled) setFound(n => n + 1);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [customerId, orderPhone, locationId, auth?.accessToken, axiosprivate]);

  if (!customerId) return null;
  return orderPhone || phoneByCustomerId.get(customerId) || null;
};

export default useCustomerPhone;
